import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

export function compressImageUpload(file, callback) {
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (ev) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const MAX_WIDTH = 1280;
      const MAX_HEIGHT = 1280;
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width;
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width *= MAX_HEIGHT / height;
          height = MAX_HEIGHT;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      // Compress to 60% quality JPEG
      const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
      callback(dataUrl);
    };
    img.src = ev.target.result;
  };
  reader.readAsDataURL(file);
}

/**
 * Uploads an image to the sanitization API to strip metadata and re-encode it.
 * @param {File} file The raw file from input
 * @param {string} bucket Target storage bucket
 * @param {string} path Target path in bucket
 * @returns {Promise<string>} The clean public URL
 */
export async function uploadSanitizedImage(file, bucket, path) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const base64 = ev.target.result;
        const { supabase } = await import('./supabase');
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session?.access_token) {
          throw new Error('Sign in again before uploading media.');
        }

        let signedBase64 = base64;
        try {
          const { data: { user } } = await supabase.auth.getUser();
          const c2paRes = await fetch('/api/v1/c2pa-sign', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ imageBase64: base64, did: user?.id })
          });
          if (c2paRes.ok) {
            const c2paData = await c2paRes.json();
            signedBase64 = c2paData.signedImageBase64 || base64;
            console.log('[C2PA] Image successfully stamped with content credentials:', c2paData.signature);
          }
        } catch (err) {
          console.warn('[C2PA] Watermarking failed, continuing with original.', err);
        }

        const res = await fetch('/api/safety?action=sanitize-image', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ imageBase64: signedBase64, bucket, path, preserveMetadata: true })
        });

        if (!res.ok) {
          let errorMessage = 'Sanitization failed';
          try {
            const errBody = await res.json();
            errorMessage = errBody.error || errBody.message || errorMessage;
          } catch {
            // If not JSON, get raw text (might be a Vercel error page)
            const text = await res.text().catch(() => '');
            console.error('[Sanitize API] Raw Error Response:', text);
            errorMessage = `Server Error (${res.status})`;
          }
          throw new Error(errorMessage);
        }

        const data = await res.json();
        resolve(data.url);
      } catch (err) {
        console.error('[Sanitize API] Upload Failed:', err);
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export async function uploadProfileVideo(file, bucket, path) {
  if (!file) {
    throw new Error('Choose a video file first.');
  }

  const allowedTypes = new Set(['video/mp4', 'video/webm', 'video/ogg']);
  if (!allowedTypes.has(file.type)) {
    throw new Error('Use an MP4, WebM, or Ogg video. MOV/QuickTime often will not play in Chrome backgrounds.');
  }

  const { supabase } = await import('./supabase');
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('Sign in again before uploading media.');
  }

  const prepareRes = await fetch('/api/safety?action=create-media-upload', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ bucket, path, contentType: file.type }),
  });

  const uploadConfig = await prepareRes.json().catch(() => ({}));
  if (!prepareRes.ok) {
    throw new Error(uploadConfig.error || 'Could not prepare video upload.');
  }

  const { error: uploadError } = await supabase.storage
    .from(uploadConfig.bucket || bucket)
    .uploadToSignedUrl(uploadConfig.path || path, uploadConfig.token, file, {
      contentType: file.type,
      upsert: true,
    });

  if (uploadError) {
    throw new Error(uploadError.message || 'Video upload failed.');
  }

  if (uploadConfig.publicUrl) {
    return uploadConfig.publicUrl;
  }

  const { data } = supabase.storage
    .from(uploadConfig.bucket || bucket)
    .getPublicUrl(uploadConfig.path || path);

  if (!data?.publicUrl) {
    throw new Error('Video uploaded, but no public URL was returned.');
  }

  return data.publicUrl;
}

